/**
 * ListView Utility Functions
 *
 * Pure utility functions for FormCrafterListView data processing.
 * Includes formatting utilities and field editing helpers.
 */

import { convertUTCToLocal } from '../../components/utils/dateUtils';
import {
  FIELD_TYPES,
  CONTAINER_FIELD_TYPES,
  DATE_FIELD_TYPES,
} from '../../constants/formCrafter.constant';

/**
 * Format a date value for display in list view
 * @param {any} value - The date value to format
 * @param {string} fieldType - The type of field (date, datetime-local, time, etc.)
 * @returns {string} - Formatted date string or '-' if empty
 */
export const formatDateValue = (value, fieldType) => {
  if (!value || value === '' || value === null || value === undefined) {
    return '-';
  }

  try {
    let date;

    if (typeof value === 'string') {
      // For date-only fields in YYYY-MM-DD format
      if (fieldType === 'date' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(`${value}T00:00:00`);
      } else if (fieldType === 'time' && value.match(/^\d{2}:\d{2}/)) {
        // Time only - return as is
        return value;
      } else {
        date = new Date(value);
      }
    } else if (value instanceof Date) {
      date = value;
    } else {
      date = new Date(value);
    }

    if (isNaN(date.getTime())) {
      return String(value);
    }

    if (fieldType === 'date') {
      return date.toLocaleDateString('en-GB');
    } else if (fieldType === 'time') {
      return date
        .toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
        .toUpperCase();
    } else {
      return convertUTCToLocal(value);
    }
  } catch (e) {
    return String(value);
  }
};

/**
 * Format a hidden field value for display
 * @param {any} value - The value to format
 * @returns {string} - Formatted value or '-' if empty
 */
export const formatHiddenFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // Check if it's an ISO date/time string
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
  ) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return convertUTCToLocal(value);
      }
    } catch (e) {
      // If parsing fails, return the original value
    }
  }

  return value;
};

/**
 * Switch value constants for translation
 */
export const SWITCH_VALUES = {
  YES: 'yes',
  NO: 'no',
  EMPTY: 'empty',
};

/**
 * Format a switch field value for display
 * Returns a key constant that should be translated by the component
 * @param {any} value - The switch value
 * @returns {string} - SWITCH_VALUES.YES, SWITCH_VALUES.NO, or SWITCH_VALUES.EMPTY
 */
export const formatSwitchValue = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return SWITCH_VALUES.YES;
  }

  if (
    value === false ||
    value === 'false' ||
    value === 0 ||
    value === '0' ||
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'object' &&
      value !== null &&
      Object.keys(value).length === 0)
  ) {
    return SWITCH_VALUES.NO;
  }

  return SWITCH_VALUES.EMPTY;
};

/**
 * Format a textarea value preserving line breaks
 * @param {any} value - The textarea value
 * @returns {string} - Formatted value or '-' if empty
 */
export const formatTextareaValue = (value) => {
  if (value === '' || value === null || value === undefined) {
    return '-';
  }
  return String(value);
};

/**
 * Check if a value is a valid base64 signature
 * @param {string} value - The value to check
 * @returns {boolean} - True if it appears to be base64 signature data
 */
export const isBase64Signature = (value) => {
  if (!value || typeof value !== 'string') return false;

  return (
    value.startsWith('data:image/') ||
    (value.length > 100 && /^[A-Za-z0-9+/=]+$/.test(value.replace(/\s/g, '')))
  );
};

/**
 * Get the image source for a base64 signature
 * @param {string} value - The base64 value
 * @returns {string} - Data URL for the image
 */
export const getBase64ImageSrc = (value) => {
  return value.startsWith('data:') ? value : `data:image/png;base64,${value}`;
};

/**
 * Process file metadata from various sources into a unified format
 * @param {Array} fileMetadata - File metadata from props
 * @param {Object} filledForm - Filled form object with potential fileMetadata
 * @returns {Object} - File metadata organized by subformId_fieldId key
 */
export const processFileMetadata = (
  fileMetadata,
  filledForm,
  formId = null
) => {
  const metadata = {};
  const processedFileIds = new Set();

  const processFile = (file) => {
    if (
      formId &&
      file.form_id !== undefined &&
      file.form_id !== null &&
      String(file.form_id) !== String(formId)
    ) {
      return;
    }
    if (!file.id || processedFileIds.has(file.id)) return;
    processedFileIds.add(file.id);

    const subformId = String(
      file.subform_id || file.subformId || file.subFormId || ''
    );
    const fieldId =
      file.field_id || file.fieldId || file.context?.field_id || '';
    const key = `${subformId}_${fieldId}`;

    if (!metadata[key]) metadata[key] = [];
    metadata[key].push({
      id: file.id,
      name:
        file.original_name ||
        file.file_path?.split('/').pop() ||
        file.path?.split('/').pop(),
      filename:
        file.original_name ||
        file.file_path?.split('/').pop() ||
        file.path?.split('/').pop(),
      path: file.file_path || file.path,
      mimeType: file.mime_type || file.mimeType,
      size: parseInt(file.file_size, 10) || parseInt(file.size, 10) || 0,
    });
  };

  // Process fileMetadata from prop
  if (Array.isArray(fileMetadata)) {
    fileMetadata.forEach(processFile);
  }

  // Process fileMetadata from filledForm
  if (filledForm?.fileMetadata && Array.isArray(filledForm.fileMetadata)) {
    filledForm.fileMetadata.forEach(processFile);
  }

  return metadata;
};

/**
 * Check if a field type is a date-related type
 * @param {string} fieldType - The field type
 * @returns {boolean} - True if it's a date-related field
 */
export const isDateFieldType = (fieldType) => {
  return DATE_FIELD_TYPES.includes(fieldType);
};

/**
 * Extracts formId from filledForm or uses provided formId
 * @param {number|string} formId - Explicit form ID
 * @param {Object} filledForm - Filled form object
 * @returns {string|null} - Resolved form ID
 */
export const resolveFormId = (formId, filledForm) => {
  if (formId) return formId;

  if (filledForm?.value) {
    try {
      const parsed =
        typeof filledForm.value === 'string'
          ? JSON.parse(filledForm.value)
          : filledForm.value;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const firstKey = Object.keys(parsed)[0];
        if (firstKey) return firstKey;
      }
    } catch (error) {
      console.warn('Could not extract formId from filledForm.value:', error);
    }
  }
  return null;
};

/**
 * Creates a composite field key for state management
 * @param {string|number} formId - Form ID
 * @param {string|number} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @returns {string|null} - Composite key or null if invalid
 */
export const makeFieldKey = (formId, subformId, fieldId) => {
  if (!formId || !subformId || !fieldId) return null;
  return `${formId}_${subformId}_${fieldId}`;
};

/**
 * Parses subform schema from json_object
 * @param {Object} subform - Subform object with json_object property
 * @returns {Object|null} - Parsed schema or null
 */
export const parseSubformSchema = (subform) => {
  if (!subform?.json_object) return null;
  try {
    return typeof subform.json_object === 'string'
      ? JSON.parse(subform.json_object)
      : subform.json_object;
  } catch (error) {
    console.error('Error parsing subform schema:', error, subform);
    return null;
  }
};

/**
 * Checks if a field type is a container-like type (non-editable)
 * @param {string} fieldType - Field type string
 * @returns {boolean}
 */
export const isContainerType = (fieldType) => {
  return CONTAINER_FIELD_TYPES.includes(fieldType);
};

/**
 * Determines if a field can be edited inline
 * @param {string} fieldType - Field type
 * @param {boolean} enableFieldEdit - Whether editing is enabled
 * @param {Function} isFileFieldType - Function to check file field types (unused, kept for API compatibility)
 * @param {Object} field - Optional field object (unused, kept for API compatibility)
 * @returns {boolean}
 */
export const canEditFieldType = (
  fieldType,
  enableFieldEdit,
  isFileFieldType
) => {
  if (!enableFieldEdit) return false;
  if (isContainerType(fieldType)) return false;
  return true;
};

/**
 * Filters subforms based on activeSubformIndex
 * @param {Array} subforms - Array of subforms
 * @param {number} activeSubformIndex - Optional index to filter by
 * @returns {Array} - Filtered subforms
 */
export const getSubformsToRender = (subforms, activeSubformIndex) => {
  const safeSubforms = Array.isArray(subforms) ? subforms : [];
  if (activeSubformIndex !== undefined) {
    return [safeSubforms[activeSubformIndex]].filter(Boolean);
  }
  return safeSubforms;
};

/**
 * Parses a composite field key back into its components
 * Key format: formId_subformId_fieldId (field IDs may contain underscores)
 * @param {string} key - Composite key
 * @param {string|number} formId - Known form ID to help parse
 * @param {string|number} subformId - Known subform ID to help parse
 * @returns {Object|null} - { formId, subformId, fieldId } or null
 */
export const parseFieldKey = (key, formId, subformId) => {
  if (!key || !formId || !subformId) return null;

  const prefix = `${formId}_${subformId}_`;
  if (!key.startsWith(prefix)) return null;

  const fieldId = key.slice(prefix.length);
  return { formId: String(formId), subformId: String(subformId), fieldId };
};

/**
 * Computes effective values for a subform by overlaying inline edits
 * @param {Object} subformData - Original subform data
 * @param {Object} editedValues - Confirmed inline edits (keyed by composite key)
 * @param {Object} editingDrafts - Current editing drafts (keyed by composite key)
 * @param {string|number} formId - Form ID
 * @param {string|number} subformId - Subform ID
 * @returns {Object} - Merged values for visibility evaluation
 */
export const getEffectiveValues = (
  subformData,
  editedValues,
  editingDrafts,
  formId,
  subformId
) => {
  const effective = { ...subformData };
  const prefix = `${formId}_${subformId}_`;

  // Overlay confirmed edits
  Object.keys(editedValues || {}).forEach((key) => {
    if (key.startsWith(prefix)) {
      const fieldId = key.slice(prefix.length);
      effective[fieldId] = editedValues[key];
    }
  });

  // Overlay current drafts (takes precedence over confirmed edits)
  Object.keys(editingDrafts || {}).forEach((key) => {
    if (key.startsWith(prefix)) {
      const fieldId = key.slice(prefix.length);
      effective[fieldId] = editingDrafts[key];
    }
  });

  return effective;
};

/**
 * Checks if a field has visibility conditions
 * @param {Object} field - Field schema
 * @returns {boolean}
 */
export const hasConditions = (field) => {
  return (
    field?.conditions &&
    Array.isArray(field.conditions) &&
    field.conditions.length > 0
  );
};

/**
 * Gets the effective field type for rendering
 * Hidden fields are rendered as text fields (only visible to reviewers)
 * @param {Object} field - Field schema
 * @returns {string} - Effective field type
 */
export const getEffectiveFieldType = (field) => {
  if (!field) return FIELD_TYPES.TEXT;
  return field.type === FIELD_TYPES.HIDDEN ? FIELD_TYPES.TEXT : field.type;
};

/**
 * Creates an effective field object with the correct type for rendering
 * @param {Object} field - Original field schema
 * @returns {Object} - Field with effective type
 */
export const getEffectiveField = (field) => {
  if (!field) return field;
  const effectiveType = getEffectiveFieldType(field);
  return effectiveType !== field.type
    ? { ...field, type: effectiveType }
    : field;
};

/**
 * Builds a dependency graph: sourceFieldId -> Set of dependent field IDs
 * @param {Array} fields - Array of field schemas
 * @returns {Map} - Map of sourceFieldId -> Set(dependentFieldIds)
 */
export const buildDependencyGraph = (fields) => {
  const graph = new Map();

  if (!Array.isArray(fields)) return graph;

  fields.forEach((field) => {
    if (!hasConditions(field)) return;

    field.conditions.forEach((condition) => {
      const sourceId = condition.sourceFieldId;
      if (!sourceId) return;

      if (!graph.has(sourceId)) {
        graph.set(sourceId, new Set());
      }
      graph.get(sourceId).add(field.id);
    });
  });

  return graph;
};

/**
 * Gets all transitive dependents of a field using BFS
 * @param {string} fieldId - Starting field ID
 * @param {Map} dependencyGraph - Dependency graph from buildDependencyGraph
 * @returns {Set} - Set of all dependent field IDs (transitive)
 */
export const getTransitiveDependents = (fieldId, dependencyGraph) => {
  const result = new Set();
  const queue = [fieldId];
  const visited = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (visited.has(current)) continue;
    visited.add(current);

    const directDependents = dependencyGraph.get(current);
    if (directDependents) {
      directDependents.forEach((depId) => {
        result.add(depId);
        if (!visited.has(depId)) {
          queue.push(depId);
        }
      });
    }
  }

  return result;
};

/**
 * Check if field should be visible in applicant view
 * Hidden fields are ALWAYS hidden for applicants (conditions ignored)
 * @param {Object} field - Field schema
 * @param {Object} effectiveValues - Current form values
 * @param {Function} isFieldVisible - Visibility check function
 * @returns {boolean}
 */
export const isFieldVisibleInApplicantView = (
  field,
  effectiveValues,
  isFieldVisible
) => {
  if (field.type === FIELD_TYPES.HIDDEN) {
    return false;
  }
  if (!isFieldVisible(field, effectiveValues)) {
    return false;
  }
  return true;
};

/**
 * Check if field should be visible in reviewer view
 * Hidden fields are ALWAYS visible for reviewers (conditions ignored)
 * @param {Object} field - Field schema
 * @param {Object} effectiveValues - Current form values
 * @param {Function} isFieldVisible - Visibility check function
 * @returns {boolean}
 */
export const isFieldVisibleInReviewerView = (
  field,
  effectiveValues,
  isFieldVisible
) => {
  if (field.type === FIELD_TYPES.HIDDEN) {
    return true;
  }
  if (hasConditions(field)) {
    return isFieldVisible(field, effectiveValues);
  }
  return true;
};

/**
 * Check if a comment's field is visible based on visibility conditions
 * @param {Object} comment - Comment object with field_id and subForm_id
 * @param {Array} subforms - Array of subform objects with json_object
 * @param {Object} formData - Current form data keyed by subformId
 * @param {Function} isFieldVisible - Visibility check function from visibilityUtils
 * @returns {boolean} - Whether the comment's field is visible
 */
export const isCommentFieldVisible = (
  comment,
  subforms,
  formData,
  isFieldVisible
) => {
  if (!comment?.field_id || !comment?.subForm_id) return true;

  const subform = subforms?.find(
    (s) => String(s?.id) === String(comment.subForm_id)
  );
  if (!subform) return true;

  const schema = parseSubformSchema(subform);
  if (!schema?.fields) return true;

  const field = schema.fields.find((f) => f?.id === comment.field_id);
  if (!field) return true;

  if (!hasConditions(field)) return true;

  const subformData = formData?.[comment.subForm_id] || {};
  return isFieldVisible(field, subformData);
};

/**
 * Filter comments to only include those on visible fields
 * @param {Array} comments - Array of comment objects
 * @param {Array} subforms - Array of subform objects with json_object
 * @param {Object} formData - Current form data keyed by subformId
 * @param {Function} isFieldVisible - Visibility check function
 * @returns {Array} - Filtered comments on visible fields only
 */
export const filterVisibleFieldComments = (
  comments,
  subforms,
  formData,
  isFieldVisible
) => {
  if (!comments || !Array.isArray(comments)) return [];
  return comments.filter((comment) =>
    isCommentFieldVisible(comment, subforms, formData, isFieldVisible)
  );
};

/**
 * Get comment status for subforms, considering field visibility
 * @param {Array} comments - Array of comment objects
 * @param {Array} subforms - Array of subform objects with json_object
 * @param {Object} formData - Current form data keyed by subformId
 * @param {Function} isFieldVisible - Visibility check function
 * @returns {Object} - Comment status keyed by subform ID
 */
export const getVisibleFieldCommentStatus = (
  comments,
  subforms,
  formData,
  isFieldVisible
) => {
  const visibleComments = filterVisibleFieldComments(
    comments,
    subforms,
    formData,
    isFieldVisible
  );

  const commentStatus = {};
  visibleComments.forEach((comment) => {
    if (comment.subForm_id) {
      commentStatus[comment.subForm_id] = true;
    }
  });
  return commentStatus;
};

/**
 * Format repeater items for display
 * @param {Array} items - Repeater items array
 * @param {Function} t - Translation function
 * @returns {string} - Formatted display string
 */
export const formatRepeaterDisplayValue = (items, t) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return t('formRenderer.repeaterNoItems');
  }

  const itemCount = items.length;
  const itemsText = items
    .map(
      (item, idx) =>
        `${t('formRenderer.repeaterItem', { index: idx + 1 })}: ${item.value || '-'}`
    )
    .join(', ');

  return t('formRenderer.repeaterItemsSummary', {
    count: itemCount,
    summary: itemsText,
  });
};
